// src/components/onboarding/OnboardingWizard.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import authService             from "../../services/authService";
import condominiosService      from "../../services/condominiosService";
import contasBancariasService  from "../../services/contasBancariasService";
import unidadesService         from "../../services/unidadesService";
import contasContabeisService  from "../../services/contasContabeisService";

// ── helpers ───────────────────────────────────────────────────────────────
const onlyDigits = v => (v || "").replace(/\D/g, "");
const fmtCNPJ = v => onlyDigits(v).replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, "$1.$2.$3/$4-$5").substring(0, 18);
const fmtCEP  = v => onlyDigits(v).replace(/(\d{5})(\d{3}).*/, "$1-$2").substring(0, 9);
const fmtPhone = v => {
  const d = onlyDigits(v).substring(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4}).*/, "($1) $2-$3").replace(/-$/, "");
  return d.replace(/(\d{2})(\d{5})(\d{0,4}).*/, "($1) $2-$3").replace(/-$/, "");
};
const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

// Validador correto de CNPJ
function validarCNPJ(v) {
  const s = onlyDigits(v);
  if (s.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(s)) return false; // todos iguais
  const calc = (n) => {
    let sum = 0, pos = n - 7;
    for (let i = n; i >= 1; i--) {
      sum += parseInt(s[n - i]) * pos--;
      if (pos < 2) pos = 9;
    }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return calc(12) === parseInt(s[12]) && calc(13) === parseInt(s[13]);
}
function validarEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || ""); }

const BANCOS = [
  { code:"756", name:"Sicoob" },
  { code:"364", name:"EFÍ Bank (Gerencianet)" },
  { code:"001", name:"Banco do Brasil" },
  { code:"033", name:"Santander" },
  { code:"104", name:"Caixa Econômica" },
  { code:"237", name:"Bradesco" },
  { code:"341", name:"Itaú" },
  { code:"041", name:"Banrisul" },
  { code:"260", name:"Nubank" },
  { code:"077", name:"Inter" },
];

const STEPS = [
  { id:"welcome",       icon:"🎉", title:"Bem-vindo ao Horizon eCondo!",   sub:"Vamos configurar seu sistema em poucos minutos.", short:"Bem-vindo" },
  { id:"condominio",    icon:"🏢", title:"1 · Cadastre o Condomínio",  sub:"Dados essenciais para começar.",                  short:"Condomínio" },
  { id:"plano-contas",  icon:"📊", title:"2 · Plano de Contas",        sub:"Importamos um modelo completo.",                  short:"Plano de Contas" },
  { id:"conta-bancaria",icon:"🏦", title:"3 · Conta Bancária",         sub:"Necessária para boletos.",                        short:"Conta" },
  { id:"unidades",      icon:"🏠", title:"4 · Unidades",               sub:"Estrutura básica do condomínio.",                 short:"Unidades" },
  { id:"done",          icon:"✅", title:"Sistema configurado!",        sub:"Tudo pronto para começar.",                       short:"Pronto" },
];

// ── Stepper ───────────────────────────────────────────────────────────────
function Stepper({ current, passosFeitos }) {
  const dots = STEPS.slice(1, -1);
  return (
    <div style={{display:"flex",alignItems:"center",gap:0,padding:"20px 32px 0"}}>
      {dots.map((s, i) => {
        const num    = i + 1;
        const done   = num < current || passosFeitos?.[s.id];
        const active = num === current;
        return (
          <React.Fragment key={s.id}>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",flex:"none"}}>
              <div style={{
                width:34,height:34,borderRadius:"50%",display:"flex",alignItems:"center",
                justifyContent:"center",fontWeight:700,fontSize:13,
                background: done ? "#10B981" : active ? "#1B3A6B" : "#E2E8F0",
                color: done || active ? "#fff" : "#94A3B8",
                border: active ? "2px solid #1B3A6B" : "none",
                transition:"all .3s",boxSizing:"border-box",
              }}>{done ? "✓" : num}</div>
              <div style={{fontSize:10,marginTop:4,color:active?"#1B3A6B":"#94A3B8",fontWeight:active?700:400,whiteSpace:"nowrap"}}>
                {s.short}
              </div>
            </div>
            {i < dots.length - 1 && (
              <div style={{flex:1,height:2,background:done?"#10B981":"#E2E8F0",margin:"0 4px",marginBottom:20,transition:"background .3s"}}/>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── main wizard ───────────────────────────────────────────────────────────
export default function OnboardingWizard({ onClose, onNavigate }) {
  const { usuario } = useAuth();
  const storageKey  = `onboarding_done_${usuario?.idCliente || usuario?.id}`;

  const [step,    setStep]    = useState(0);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [cnpjErro, setCnpjErro] = useState("");
  const [emailErro, setEmailErro] = useState("");
  const [cepLoading, setCepLoading] = useState(false);
  const [condoCriado,    setCondoCriado]    = useState(null);
  const [statusBackend,  setStatusBackend]  = useState(null);

  // ── forms ────────────────────────────────────────────────────────────
  const [condo, setCondo] = useState({
    nmCondominio:"", nmFantasia:"", cnpj:"",
    logradouro:"", numero:"", complemento:"", bairro:"", cidade:"", uf:"SP", cep:"",
    email:"", telefone:"", tpCobranca:"RateioFracaoIdeal", diaVencimento:"10",
  });
  const [conta, setConta] = useState({
    nmConta:"Conta Principal", banco:"756", nomeBanco:"Sicoob",
    agencia:"", contaCorrente:"", tpConta:"ContaCorrente", saldoInicial:"0",
  });
  const [modoUnidade,  setModoUnidade]  = useState("andares"); // "faixa" | "andares" | "blocos"
  const [qtdAndares,   setQtdAndares]   = useState("5");
  const [qtdPorAndar,  setQtdPorAndar]  = useState("4");
  const [faixaDe,      setFaixaDe]      = useState("101");
  const [faixaAte,     setFaixaAte]     = useState("120");
  const [blocos,       setBlocos]       = useState("A,B");

  const set  = (k,v) => setCondo(p=>({...p,[k]:v}));
  const setB = (k,v) => setConta(p=>({...p,[k]:v}));

  // ─── Inicialização: descobre o que já foi feito no backend e pula passos
  useEffect(() => {
    let cancelled = false;
    authService.onboardingStatus().then(r => {
      if (cancelled || !r) return;
      setStatusBackend(r);
      // Recupera id do condomínio criado anteriormente
      if (r.idPrimeiroCondominio && !condoCriado) {
        setCondoCriado({ id: r.idPrimeiroCondominio });
      }
      // Decide qual passo abrir
      const saved = localStorage.getItem(`onboarding_progress_${storageKey}`);
      const savedStep = saved ? (JSON.parse(saved).step || 0) : 0;
      // Se há passos já feitos no servidor mas o usuário não tinha progress local, pula até o primeiro pendente
      if (!saved && r.passos) {
        let s = 1;
        if (r.passos.condominio)    s = 2;
        if (r.passos.planoContas)   s = 3;
        if (r.passos.contaBancaria) s = 4;
        if (r.passos.unidades)      s = 5;
        setStep(Math.max(savedStep, s));
      } else {
        setStep(savedStep);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  // ─── Resume progress local
  useEffect(() => {
    const saved = localStorage.getItem(`onboarding_progress_${storageKey}`);
    if (saved) {
      try {
        const d = JSON.parse(saved);
        if (d.condoCriado) setCondoCriado(d.condoCriado);
        if (d.condo) setCondo(prev => ({ ...prev, ...d.condo }));
        if (d.conta) setConta(prev => ({ ...prev, ...d.conta }));
      } catch {}
    }
  }, [storageKey]);

  const save = useCallback((s, extra = {}) => {
    localStorage.setItem(
      `onboarding_progress_${storageKey}`,
      JSON.stringify({ step: s, condoCriado, condo, conta, ...extra })
    );
  }, [storageKey, condoCriado, condo, conta]);

  // ─── Autocomplete CEP via ViaCEP
  const buscarCep = async (cep) => {
    const d = onlyDigits(cep);
    if (d.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${d}/json/`);
      const j = await r.json();
      if (!j.erro) {
        setCondo(p => ({
          ...p,
          logradouro: j.logradouro || p.logradouro,
          bairro:     j.bairro     || p.bairro,
          cidade:     j.localidade || p.cidade,
          uf:         j.uf         || p.uf,
        }));
      }
    } catch {}
    setCepLoading(false);
  };

  // ── actions ───────────────────────────────────────────────────────────
  const handleCondo = async () => {
    if (!condo.nmCondominio.trim()) return setError("Nome do condomínio é obrigatório.");
    const cnpjDigits = onlyDigits(condo.cnpj);
    if (cnpjDigits.length !== 14)    return setError("CNPJ deve ter 14 dígitos.");
    if (!validarCNPJ(condo.cnpj))    return setError("CNPJ inválido. Verifique os dígitos verificadores.");
    if (condo.email && !validarEmail(condo.email)) return setError("Email inválido.");
    setSaving(true); setError("");
    try {
      // Se já criou antes, apenas avança
      if (condoCriado?.id) {
        const n = step + 1; setStep(n); save(n);
      } else {
        const r = await condominiosService.criar({
          ...condo,
          cnpj: cnpjDigits,
          cep: onlyDigits(condo.cep),
        });
        const created = r.data || r;
        setCondoCriado(created);
        const n = step + 1; setStep(n); save(n, { condoCriado: created });
      }
    } catch (e) {
      setError(e.message || "Erro ao salvar condomínio.");
    }
    setSaving(false);
  };

  const handlePlano = async () => {
    setSaving(true); setError("");
    try {
      if (condoCriado?.id) {
        await contasContabeisService.importarPlanoPadrao(condoCriado.id);
      }
      const n = step + 1; setStep(n); save(n);
    } catch (e) {
      // Não bloqueia
      const n = step + 1; setStep(n); save(n);
    }
    setSaving(false);
  };

  const handleConta = async () => {
    if (!conta.agencia.trim() || !conta.contaCorrente.trim())
      return setError("Agência e conta corrente são obrigatórias.");
    setSaving(true); setError("");
    try {
      await contasBancariasService.criar({
        ...conta,
        idCondominio: condoCriado?.id,
        saldoInicial: parseFloat(conta.saldoInicial || 0),
      });
      const n = step + 1; setStep(n); save(n);
    } catch (e) {
      setError(e.message || "Erro ao salvar conta.");
    }
    setSaving(false);
  };

  // Gera lista de unidades conforme modo selecionado
  const gerarListaUnidades = useCallback(() => {
    if (modoUnidade === "faixa") {
      const de = parseInt(faixaDe) || 1;
      const ate = parseInt(faixaAte) || de;
      if (ate < de) return [];
      return Array.from({ length: ate - de + 1 }, (_, i) => ({
        unidade: String(de + i), bloco: "",
      }));
    }
    if (modoUnidade === "blocos") {
      const bList = blocos.split(",").map(b => b.trim()).filter(Boolean);
      const andares  = Math.min(50, parseInt(qtdAndares) || 1);
      const porAndar = Math.min(30, parseInt(qtdPorAndar) || 1);
      const list = [];
      for (const bloco of bList)
        for (let a = 1; a <= andares; a++)
          for (let u = 1; u <= porAndar; u++)
            list.push({ unidade: String(a * 100 + u), bloco });
      return list;
    }
    // andares (default)
    const andares  = Math.min(50, parseInt(qtdAndares) || 1);
    const porAndar = Math.min(30, parseInt(qtdPorAndar) || 1);
    const list = [];
    for (let a = 1; a <= andares; a++)
      for (let u = 1; u <= porAndar; u++)
        list.push({ unidade: String(a * 100 + u), bloco: "" });
    return list;
  }, [modoUnidade, qtdAndares, qtdPorAndar, faixaDe, faixaAte, blocos]);

  // Lista calculada — sempre reflete o modo atual
  const listaUnidades = useMemo(() => gerarListaUnidades(), [gerarListaUnidades]);
  const totalUnidades = listaUnidades.length;

  const handleUnidades = async () => {
    if (!condoCriado?.id) return handleConcluir();
    const lista = listaUnidades;
    if (!lista.length) return setError("Configure as unidades antes de continuar.");
    if (lista.length > 200 && !window.confirm(`Você vai criar ${lista.length} unidades. Confirma?`)) return;
    setSaving(true); setError("");
    try {
      await unidadesService.criarBulk(condoCriado.id, lista);
      const n = step + 1; setStep(n); save(n);
    } catch (e) {
      setError(e.message || "Erro ao criar unidades.");
    }
    setSaving(false);
  };

  const handleConcluir = () => {
    localStorage.setItem(storageKey, "done");
    localStorage.removeItem(`onboarding_progress_${storageKey}`);
    onClose();
    if (onNavigate) setTimeout(() => onNavigate("dashboard"), 300);
  };

  const skip = () => { const n = step + 1; setStep(n); save(n); setError(""); };

  const handleAdiarParaDepois = () => {
    save(step);
    onClose();
  };

  const st = STEPS[step];

  // ─── Indicadores de "já feito no backend" para mostrar visualmente
  const jaFeito = statusBackend?.passos || {};

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9999,
      background:"rgba(15,23,42,.8)", backdropFilter:"blur(8px)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:20,
    }}>
      <div style={{
        background:"var(--bg-surface)", borderRadius:20, width:"100%", maxWidth:640,
        boxShadow:"0 40px 80px rgba(0,0,0,.35)", overflow:"hidden",
        animation:"wzUp .35s cubic-bezier(.22,.68,0,1.2)", maxHeight:"95vh",
        display:"flex", flexDirection:"column",
      }}>
        <style>{`
          @keyframes wzUp{from{transform:translateY(40px) scale(.97);opacity:0}to{transform:none;opacity:1}}
          @keyframes wzFade{from{opacity:0;transform:translateX(12px)}to{opacity:1;transform:none}}
          .wz-step{animation:wzFade .3s ease}
        `}</style>

        {/* ── Header gradient ───────────────────────────────────────────── */}
        <div style={{
          background:"linear-gradient(135deg,#0F2547 0%,#1B3A6B 60%,#1E5FAD 100%)",
          padding:"22px 28px 18px", position:"relative", flexShrink:0,
        }}>
          {step > 0 && step < STEPS.length - 1 && (
            <button onClick={handleAdiarParaDepois} title="Fechar e continuar depois" style={{
              position:"absolute", top:14, right:14, background:"rgba(255,255,255,.12)",
              border:"none", borderRadius:8, width:32, height:32, cursor:"pointer", color:"#fff",
              fontSize:18, display:"flex", alignItems:"center", justifyContent:"center",
            }}>×</button>
          )}
          <div style={{fontSize:32, marginBottom:4, lineHeight:1}}>{st.icon}</div>
          <h2 style={{color:"#fff", margin:0, fontSize:20, fontWeight:800, letterSpacing:"-.3px"}}>{st.title}</h2>
          <p style={{color:"rgba(255,255,255,.7)", margin:"4px 0 0", fontSize:13}}>{st.sub}</p>
          {statusBackend?.pctConcluido > 0 && step > 0 && step < STEPS.length - 1 && (
            <div style={{marginTop:10}}>
              <div style={{height:4, background:"rgba(255,255,255,.15)", borderRadius:2, overflow:"hidden"}}>
                <div style={{height:"100%", width:`${statusBackend.pctConcluido}%`,
                  background:"linear-gradient(90deg,#10B981,#22D3EE)", transition:"width .3s"}}/>
              </div>
              <div style={{fontSize:10, color:"rgba(255,255,255,.7)", marginTop:4}}>
                Setup geral: {statusBackend.concluidos}/{statusBackend.total} ({statusBackend.pctConcluido}%)
              </div>
            </div>
          )}
        </div>

        {/* ── Step dots ─────────────────────────────────────────────────── */}
        {step > 0 && step < STEPS.length - 1 && <Stepper current={step} passosFeitos={{
          condominio: jaFeito.condominio,
          "plano-contas": jaFeito.planoContas,
          "conta-bancaria": jaFeito.contaBancaria,
          unidades: jaFeito.unidades,
        }}/>}

        {/* ── Content ───────────────────────────────────────────────────── */}
        <div style={{padding:"20px 28px", minHeight:240, overflowY:"auto", flex:1}}>

          {/* ── Welcome ─────────────────────────────────────────────────── */}
          {step === 0 && (
            <div className="wz-step">
              {statusBackend?.pctConcluido > 0 && (
                <div style={{
                  background:"linear-gradient(135deg,#F0FDF4,#DCFCE7)", border:"1px solid #86EFAC",
                  borderRadius:10, padding:"10px 14px", marginBottom:14, display:"flex", gap:10, alignItems:"center"
                }}>
                  <i className="bi bi-check-circle-fill" style={{fontSize:20, color:"#15803D"}}/>
                  <div style={{fontSize:13, color:"#166534"}}>
                    Você já tem <strong>{statusBackend.concluidos}/{statusBackend.total}</strong> passos prontos.
                    Vamos pular para o próximo pendente.
                  </div>
                </div>
              )}
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16}}>
                {STEPS.slice(1, -1).map(s => {
                  const feito = jaFeito[s.id === "plano-contas" ? "planoContas"
                                       : s.id === "conta-bancaria" ? "contaBancaria"
                                       : s.id];
                  return (
                    <div key={s.id} style={{
                      background: feito ? "#F0FDF4" : "#F8FAFC", borderRadius:10, padding:"12px 14px",
                      border: feito ? "1px solid #86EFAC" : "1px solid #E2E8F0",
                      display:"flex", gap:10, alignItems:"flex-start",
                    }}>
                      <span style={{fontSize:20, flexShrink:0}}>{feito ? "✅" : s.icon}</span>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700, fontSize:12.5, color:"var(--text-primary)"}}>
                          {s.short}
                          {feito && <span style={{fontSize:10, color:"#15803D", marginLeft:6, fontWeight:600}}>· feito</span>}
                        </div>
                        <div style={{fontSize:11, color:"var(--gray-500)", marginTop:2}}>{s.sub}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p style={{fontSize:12.5, color:"var(--gray-600)", lineHeight:1.7, margin:0}}>
                Este assistente cobre a configuração inicial. Você pode pular qualquer etapa
                e completar depois pelo menu. Leva menos de <strong>2 minutos</strong>.
              </p>
            </div>
          )}

          {/* ── Condomínio ──────────────────────────────────────────────── */}
          {step === 1 && (
            <div className="wz-step">
              <div className="row g-2">
                <div className="col-8">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Nome do Condomínio <span style={{color:"#EF4444"}}>*</span></label>
                  <input className="form-control" value={condo.nmCondominio}
                    onChange={e=>set("nmCondominio", e.target.value)}
                    placeholder="Ex: Residencial das Flores"
                    disabled={!!condoCriado?.id}/>
                </div>
                <div className="col-4">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>CNPJ <span style={{color:"#EF4444"}}>*</span></label>
                  <input
                    className={`form-control${cnpjErro?" is-invalid":condo.cnpj && onlyDigits(condo.cnpj).length===14 && !cnpjErro?" is-valid":""}`}
                    value={condo.cnpj}
                    onChange={e=>{
                      const masked = fmtCNPJ(e.target.value);
                      set("cnpj", masked);
                      const digits = onlyDigits(masked);
                      if (digits.length === 14) setCnpjErro(validarCNPJ(masked) ? "" : "CNPJ inválido.");
                      else setCnpjErro("");
                    }}
                    disabled={!!condoCriado?.id}
                    placeholder="00.000.000/0001-00" maxLength={18}/>
                  {cnpjErro && <div className="invalid-feedback">{cnpjErro}</div>}
                  {!cnpjErro && onlyDigits(condo.cnpj).length === 14 && <div className="valid-feedback">CNPJ válido ✓</div>}
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Nome Fantasia</label>
                  <input className="form-control" value={condo.nmFantasia}
                    onChange={e=>set("nmFantasia", e.target.value)}
                    placeholder="Como o condomínio é conhecido"/>
                </div>
                <div className="col-4">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>
                    CEP {cepLoading && <span className="spinner-border spinner-border-sm ms-1" style={{width:10,height:10}}/>}
                  </label>
                  <input className="form-control" value={condo.cep}
                    onChange={e=>{ const v=fmtCEP(e.target.value); set("cep", v); }}
                    onBlur={()=>buscarCep(condo.cep)}
                    placeholder="00000-000" maxLength={9}/>
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Logradouro</label>
                  <input className="form-control" value={condo.logradouro}
                    onChange={e=>set("logradouro", e.target.value)} placeholder="Rua, Av, Alameda..."/>
                </div>
                <div className="col-2">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Número</label>
                  <input className="form-control" value={condo.numero} onChange={e=>set("numero", e.target.value)}/>
                </div>
                <div className="col-5">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Bairro</label>
                  <input className="form-control" value={condo.bairro}
                    onChange={e=>set("bairro", e.target.value)}/>
                </div>
                <div className="col-5">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Cidade</label>
                  <input className="form-control" value={condo.cidade}
                    onChange={e=>set("cidade", e.target.value)}/>
                </div>
                <div className="col-2">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>UF</label>
                  <select className="form-select" value={condo.uf}
                    onChange={e=>set("uf", e.target.value)}>
                    {UFS.map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Email de contato</label>
                  <input className={`form-control${emailErro?" is-invalid":""}`} type="email"
                    value={condo.email}
                    onChange={e=>{ set("email", e.target.value); setEmailErro(""); }}
                    onBlur={()=>{ if (condo.email && !validarEmail(condo.email)) setEmailErro("Email inválido."); }}
                    placeholder="sindico@condominio.com.br"/>
                  {emailErro && <div className="invalid-feedback">{emailErro}</div>}
                </div>
                <div className="col-3">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Telefone</label>
                  <input className="form-control" value={condo.telefone}
                    onChange={e=>set("telefone", fmtPhone(e.target.value))}
                    placeholder="(11) 99999-9999" maxLength={15}/>
                </div>
                <div className="col-3">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Dia de Vencimento</label>
                  <select className="form-select" value={condo.diaVencimento}
                    onChange={e=>set("diaVencimento", e.target.value)}>
                    {[1,5,8,10,15,20,25,28].map(d => <option key={d} value={d}>Dia {d}</option>)}
                  </select>
                </div>
              </div>
              {condoCriado?.id && (
                <div className="alert alert-info py-2 px-3 mt-3 mb-0" style={{fontSize:12}}>
                  <i className="bi bi-info-circle me-1"/>
                  Condomínio já criado. Clique em <strong>"Próximo →"</strong> para continuar.
                </div>
              )}
            </div>
          )}

          {/* ── Plano de Contas ─────────────────────────────────────────── */}
          {step === 2 && (
            <div className="wz-step">
              <div style={{
                background:"linear-gradient(135deg,#EFF6FF,#F0FDF4)",
                borderRadius:14, padding:"18px 22px", marginBottom:14,
                border:"1px solid #BFDBFE", textAlign:"center",
              }}>
                <div style={{fontSize:36, marginBottom:6}}>📋</div>
                <div style={{fontWeight:700, fontSize:14, color:"var(--text-primary)", marginBottom:6}}>
                  Plano de Contas Padrão para Condomínios
                </div>
                <div style={{fontSize:12, color:"var(--gray-600)", lineHeight:1.7}}>
                  Vamos importar um plano completo com grupos de <strong>receitas</strong> e <strong>despesas</strong>{" "}
                  adequado para condomínios. Personalize depois em <em>Plano de Contas</em>.
                </div>
              </div>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8}}>
                {[
                  { icon:"💰", label:"Cota Condominial",       sub:"e Fundo de Reserva" },
                  { icon:"📈", label:"Receitas Extraordinárias", sub:"Multas, juros, locações" },
                  { icon:"🔧", label:"Despesas Operacionais",   sub:"Manutenção, serviços" },
                  { icon:"👥", label:"Despesas com Pessoal",    sub:"Salários e encargos" },
                  { icon:"🏦", label:"Controle Bancário",       sub:"Bancos, caixa, fundos" },
                  { icon:"📑", label:"Contas a Pagar/Receber",  sub:"Passivo e contas de crédito" },
                ].map(item => (
                  <div key={item.label} style={{
                    display:"flex", gap:10, alignItems:"center",
                    padding:"8px 12px", background:"var(--bg-surface-alt)", borderRadius:8, border:"1px solid var(--border-color)"
                  }}>
                    <span style={{fontSize:18}}>{item.icon}</span>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600, fontSize:12, color:"var(--text-primary)"}}>{item.label}</div>
                      <div style={{fontSize:10.5, color:"var(--gray-500)"}}>{item.sub}</div>
                    </div>
                    <span style={{color:"#10B981", fontSize:14}}>✓</span>
                  </div>
                ))}
              </div>
              {jaFeito.planoContas && (
                <div className="alert alert-info py-2 px-3 mt-3 mb-0" style={{fontSize:12}}>
                  <i className="bi bi-info-circle me-1"/>
                  Plano de contas já configurado neste condomínio. Pode prosseguir.
                </div>
              )}
            </div>
          )}

          {/* ── Conta Bancária ──────────────────────────────────────────── */}
          {step === 3 && (
            <div className="wz-step">
              <div className="row g-2">
                <div className="col-6">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Banco</label>
                  <select className="form-select" value={conta.banco}
                    onChange={e=>{
                      const b = BANCOS.find(b=>b.code===e.target.value) || { code:e.target.value, name:e.target.value };
                      setB("banco", b.code); setB("nomeBanco", b.name);
                    }}>
                    {BANCOS.map(b => <option key={b.code} value={b.code}>{b.code} — {b.name}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Nome da Conta</label>
                  <input className="form-control" value={conta.nmConta}
                    onChange={e=>setB("nmConta", e.target.value)} placeholder="Ex: Conta Principal"/>
                </div>
                <div className="col-4">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Agência <span style={{color:"#EF4444"}}>*</span></label>
                  <input className="form-control" value={conta.agencia}
                    onChange={e=>setB("agencia", onlyDigits(e.target.value))}
                    placeholder="0000" maxLength={5}/>
                </div>
                <div className="col-5">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Conta Corrente <span style={{color:"#EF4444"}}>*</span></label>
                  <input className="form-control" value={conta.contaCorrente}
                    onChange={e=>setB("contaCorrente", e.target.value.replace(/[^\dxX\-]/g, ""))}
                    placeholder="00000000-0" maxLength={12}/>
                </div>
                <div className="col-3">
                  <label className="form-label fw-semibold" style={{fontSize:13}}>Saldo Inicial</label>
                  <input className="form-control" value={conta.saldoInicial}
                    onChange={e=>setB("saldoInicial", e.target.value)}
                    type="number" step="0.01" placeholder="0,00"/>
                </div>
              </div>
              <div className="alert alert-light py-2 px-3 mt-3 mb-0" style={{fontSize:11, color:"var(--gray-500)"}}>
                <i className="bi bi-info-circle me-1"/>
                A integração API (Sicoob/EFÍ etc.) para emitir boletos é configurada depois em <strong>Contas Bancárias → API</strong>.
              </div>
              {jaFeito.contaBancaria && (
                <div className="alert alert-info py-2 px-3 mt-2 mb-0" style={{fontSize:12}}>
                  <i className="bi bi-info-circle me-1"/>
                  Você já tem ao menos uma conta bancária. Pode pular este passo ou criar outra.
                </div>
              )}
            </div>
          )}

          {/* ── Unidades ────────────────────────────────────────────────── */}
          {step === 4 && (
            <div className="wz-step">
              <p style={{fontSize:13, color:"var(--gray-600)", marginBottom:12, lineHeight:1.6}}>
                Escolha como as unidades serão numeradas. Você pode ajustar depois em <strong>Unidades</strong>.
              </p>

              {/* Modo selector */}
              <div style={{display:"flex", gap:6, marginBottom:14}}>
                {[
                  { id:"faixa",   label:"🔢 Por Faixa",   desc:"Ex: 101 a 120" },
                  { id:"andares", label:"🏢 Por Andares", desc:"Ex: 5 andares × 4 apt" },
                  { id:"blocos",  label:"🏘️ Com Blocos",   desc:"Ex: Bloco A, B, C" },
                ].map(m => (
                  <button key={m.id} type="button" onClick={()=>setModoUnidade(m.id)}
                    style={{
                      flex:1, padding:"7px 4px", border:"2px solid", borderRadius:10,
                      cursor:"pointer", transition:"all .15s", textAlign:"center",
                      borderColor: modoUnidade===m.id ? "#1B3A6B" : "#E2E8F0",
                      background:  modoUnidade===m.id ? "#1B3A6B" : "#F8FAFC",
                      color:       modoUnidade===m.id ? "#fff"    : "#64748B",
                    }}>
                    <div style={{fontWeight:700, fontSize:12}}>{m.label}</div>
                    <div style={{fontSize:10, opacity:.75, marginTop:2}}>{m.desc}</div>
                  </button>
                ))}
              </div>

              {/* Faixa */}
              {modoUnidade === "faixa" && (
                <div style={{display:"flex", gap:12, alignItems:"flex-end", marginBottom:10}}>
                  <div style={{flex:1}}>
                    <label className="form-label fw-semibold" style={{fontSize:13}}>De</label>
                    <input type="number" className="form-control form-control-lg" value={faixaDe}
                      onChange={e=>setFaixaDe(e.target.value)} min="1"
                      style={{textAlign:"center", fontSize:20, fontWeight:700}}/>
                  </div>
                  <div style={{paddingBottom:10, fontSize:18, color:"#CBD5E1"}}>→</div>
                  <div style={{flex:1}}>
                    <label className="form-label fw-semibold" style={{fontSize:13}}>Até</label>
                    <input type="number" className="form-control form-control-lg" value={faixaAte}
                      onChange={e=>setFaixaAte(e.target.value)} min="1"
                      style={{textAlign:"center", fontSize:20, fontWeight:700}}/>
                  </div>
                </div>
              )}

              {/* Andares */}
              {modoUnidade === "andares" && (
                <div style={{display:"flex", gap:16, alignItems:"flex-start", marginBottom:10}}>
                  <div style={{flex:1}}>
                    <label className="form-label fw-semibold" style={{fontSize:13}}>Andares</label>
                    <input type="number" className="form-control form-control-lg" value={qtdAndares}
                      onChange={e=>setQtdAndares(e.target.value)} min="1" max="50"
                      style={{textAlign:"center", fontSize:20, fontWeight:700}}/>
                  </div>
                  <div style={{paddingTop:34, fontSize:20, color:"#CBD5E1"}}>×</div>
                  <div style={{flex:1}}>
                    <label className="form-label fw-semibold" style={{fontSize:13}}>Apts por andar</label>
                    <input type="number" className="form-control form-control-lg" value={qtdPorAndar}
                      onChange={e=>setQtdPorAndar(e.target.value)} min="1" max="30"
                      style={{textAlign:"center", fontSize:20, fontWeight:700}}/>
                  </div>
                </div>
              )}

              {/* Blocos */}
              {modoUnidade === "blocos" && (
                <div style={{marginBottom:10}}>
                  <label className="form-label fw-semibold" style={{fontSize:13}}>
                    Nomes dos Blocos <span style={{color:"var(--gray-400)", fontSize:11}}>(separados por vírgula)</span>
                  </label>
                  <input className="form-control" value={blocos}
                    onChange={e=>setBlocos(e.target.value)}
                    placeholder="Ex: A, B, C ou Torre 1, Torre 2"/>
                  <div style={{display:"flex", gap:12, marginTop:10}}>
                    <div style={{flex:1}}>
                      <label className="form-label fw-semibold" style={{fontSize:13}}>Andares por bloco</label>
                      <input type="number" className="form-control" value={qtdAndares}
                        onChange={e=>setQtdAndares(e.target.value)} min="1" max="50"
                        style={{textAlign:"center", fontWeight:700}}/>
                    </div>
                    <div style={{flex:1}}>
                      <label className="form-label fw-semibold" style={{fontSize:13}}>Apts por andar</label>
                      <input type="number" className="form-control" value={qtdPorAndar}
                        onChange={e=>setQtdPorAndar(e.target.value)} min="1" max="30"
                        style={{textAlign:"center", fontWeight:700}}/>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview */}
              {totalUnidades > 0 ? (
                <div style={{
                  background:"linear-gradient(135deg,#F0FDF4,#ECFDF5)",
                  borderRadius:12, padding:"12px 18px", textAlign:"center",
                  border:"1px solid #86EFAC"
                }}>
                  <div style={{fontSize:28, fontWeight:900, color:"#166534", letterSpacing:"-1px", lineHeight:1.1}}>
                    {totalUnidades} unidades
                  </div>
                  <div style={{fontSize:11, color:"#15803D", marginTop:4}}>
                    {listaUnidades.slice(0, 4).map(u => u.bloco ? `${u.bloco}-${u.unidade}` : u.unidade).join(", ")}
                    {totalUnidades > 4 ? ` ... ${listaUnidades[totalUnidades-1].bloco ? `${listaUnidades[totalUnidades-1].bloco}-${listaUnidades[totalUnidades-1].unidade}` : listaUnidades[totalUnidades-1].unidade}` : ""}
                  </div>
                  {totalUnidades > 200 && (
                    <div style={{fontSize:10, color:"#B45309", marginTop:6}}>
                      ⚠️ Volume grande — pedimos confirmação antes de criar.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{background:"var(--bg-danger-soft)", borderRadius:10, padding:"10px", textAlign:"center", color:"#991B1B", fontSize:13}}>
                  Configure as unidades para ver o preview
                </div>
              )}
              {jaFeito.unidades && (
                <div className="alert alert-info py-2 px-3 mt-3 mb-0" style={{fontSize:12}}>
                  <i className="bi bi-info-circle me-1"/>
                  Você já cadastrou unidades neste condomínio. Criar novas unidades aqui não duplicará as existentes.
                </div>
              )}
            </div>
          )}

          {/* ── Done ────────────────────────────────────────────────────── */}
          {step === 5 && (
            <div className="wz-step" style={{textAlign:"center", padding:"8px 0 4px"}}>
              <div style={{fontSize:60, marginBottom:10}}>🎊</div>
              <h3 style={{fontWeight:800, color:"#1B3A6B", marginBottom:8}}>
                {condoCriado?.nmCondominio || condo.nmCondominio || "Condomínio"} está configurado!
              </h3>
              <p style={{color:"var(--gray-600)", fontSize:13, lineHeight:1.8, marginBottom:18}}>
                Seu sistema está pronto. Os próximos passos sugeridos são:
              </p>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:14}}>
                {[
                  { icon:"bi-people", label:"Cadastrar Condôminos", nav:"condominos", color:"#1D4ED8", bg:"#EFF6FF" },
                  { icon:"bi-receipt-cutoff", label:"Gerar Boletos",  nav:"cobrancas",  color:"#166534", bg:"#DCFCE7" },
                  { icon:"bi-bar-chart-line", label:"Ver Dashboard",  nav:"dashboard",  color:"#7C3AED", bg:"#F5F3FF" },
                ].map(a => (
                  <button key={a.nav}
                    onClick={()=>{ handleConcluir(); onNavigate && setTimeout(()=>onNavigate(a.nav), 300); }}
                    style={{
                      background:a.bg, border:"1px solid "+a.color, borderRadius:10,
                      padding:"14px 8px", cursor:"pointer", textAlign:"center",
                    }}>
                    <i className={`bi ${a.icon}`} style={{fontSize:24, color:a.color, display:"block", marginBottom:4}}/>
                    <div style={{fontSize:11, color:a.color, fontWeight:700}}>{a.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Error ───────────────────────────────────────────────────── */}
          {error && (
            <div className="alert alert-danger py-2 px-3 mt-2 mb-0" style={{fontSize:13, display:"flex", gap:8, alignItems:"center"}}>
              <i className="bi bi-exclamation-triangle-fill"/>{error}
            </div>
          )}
        </div>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <div style={{
          padding:"14px 28px 18px", borderTop:"1px solid #F1F5F9",
          display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0,
        }}>
          <div style={{display:"flex", gap:10, alignItems:"center"}}>
            {step > 0 && step < STEPS.length - 1 && (
              <button className="btn btn-link text-secondary p-0" style={{fontSize:13}}
                onClick={()=>{ const n=step-1; setStep(n); save(n); setError(""); }}>
                ← Voltar
              </button>
            )}
            {step > 0 && step < STEPS.length - 1 && (
              <button className="btn btn-link text-secondary p-0" style={{fontSize:12}}
                onClick={handleAdiarParaDepois}>
                Continuar depois
              </button>
            )}
          </div>
          <div style={{display:"flex", gap:8, alignItems:"center"}}>
            {step > 0 && step < STEPS.length - 1 && step !== 1 && (
              <button className="btn btn-outline-secondary btn-sm" onClick={()=>{ skip(); setError(""); }}>
                Pular
              </button>
            )}
            {step === 0 && (
              <button className="btn btn-primary" style={{minWidth:170}}
                onClick={()=>{ setStep(1); save(1); }}>
                Começar configuração →
              </button>
            )}
            {step === 1 && (
              <button className="btn btn-primary" style={{minWidth:180}} disabled={saving} onClick={handleCondo}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2"/>Salvando...</>
                        : condoCriado?.id ? "Próximo →" : "Cadastrar Condomínio →"}
              </button>
            )}
            {step === 2 && (
              <button className="btn btn-primary" style={{minWidth:170}} disabled={saving} onClick={handlePlano}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2"/>Importando...</>
                        : jaFeito.planoContas ? "Próximo →" : "Importar Plano →"}
              </button>
            )}
            {step === 3 && (
              <button className="btn btn-primary" style={{minWidth:160}} disabled={saving} onClick={handleConta}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2"/>Salvando...</>
                        : "Salvar Conta →"}
              </button>
            )}
            {step === 4 && (
              <button className="btn btn-primary" style={{minWidth:200}} disabled={saving || totalUnidades === 0} onClick={handleUnidades}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-2"/>Criando...</>
                  : `Criar ${totalUnidades} unidade${totalUnidades===1?"":"s"} →`}
              </button>
            )}
            {step === 5 && (
              <button className="btn btn-success" style={{minWidth:160}} onClick={handleConcluir}>
                <i className="bi bi-check-lg me-1"/>Concluir
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
